import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import AgentService from '../../services/agentService';
import { supabase } from '../../config/supabase';

interface PhoneNumber {
  phone_number: string;
  provider: string;
  label: string;
  phone_number_id: string;
  assigned_agent?: {
    agent_id: string;
    agent_name: string;
  };
}

interface Agent {
  agent_id: string;
  name: string;
}

interface PhoneNumbersProps {
  apiKey: string;
}

const PhoneNumbers: React.FC<PhoneNumbersProps> = ({ apiKey }) => {
  const [phoneNumbers, setPhoneNumbers] = useState<PhoneNumber[]>([]);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAttachModal, setShowAttachModal] = useState(false);
  const [availableNumbers, setAvailableNumbers] = useState<PhoneNumber[]>([]);
  const [selectedAttachPhoneId, setSelectedAttachPhoneId] = useState<string>('');
  const [selectedAttachAgentId, setSelectedAttachAgentId] = useState<string>('');

  const fetchPhoneNumbers = useCallback(async () => {
    if (!apiKey) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // Get current user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('User not authenticated');
      }

      // Get user's phone number IDs from our tracking table
      const { data: userPhoneNumbers, error: dbError } = await supabase
        .from('user_phone_numbers')
        .select('*');

      if (dbError) {
        console.error('Error fetching user phone numbers from database:', dbError);
        setPhoneNumbers([]);
        return;
      }

      if (!userPhoneNumbers || userPhoneNumbers.length === 0) {
        setPhoneNumbers([]);
        return;
      }

      // Fetch the actual phone number details from ElevenLabs for user's phone numbers
      const phoneNumberDetails: PhoneNumber[] = [];
      
      for (const userPhone of userPhoneNumbers) {
        try {
          const response = await fetch(`https://api.elevenlabs.io/v1/convai/phone-numbers/${userPhone.elevenlabs_phone_number_id}`, {
            headers: {
              'xi-api-key': apiKey,
              'Content-Type': 'application/json',
            },
          });

          if (response.ok) {
            const phoneData = await response.json();
            phoneNumberDetails.push({
              ...phoneData,
              // Override with our stored data for consistency
              phone_number: userPhone.phone_number,
              label: userPhone.label || phoneData.label,
              provider: userPhone.provider || phoneData.provider
            });
          } else {
            // Phone number might have been deleted from ElevenLabs, clean up our database
            console.warn(`Phone number ${userPhone.elevenlabs_phone_number_id} not found in ElevenLabs, removing from database`);
            await supabase
              .from('user_phone_numbers')
              .delete()
              .eq('elevenlabs_phone_number_id', userPhone.elevenlabs_phone_number_id);
          }
        } catch (error) {
          console.error(`Error fetching phone number ${userPhone.elevenlabs_phone_number_id}:`, error);
        }
      }
      
      setPhoneNumbers(phoneNumberDetails);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch phone numbers');
    } finally {
      setLoading(false);
    }
  }, [apiKey]);

  const fetchAgents = useCallback(async () => {
    if (!apiKey) return;
    
    try {
      const agentService = new AgentService(apiKey);
      const userAgents = await agentService.getUserAgents();
      
      // Convert to the expected Agent interface format
      const agentsForDropdown = userAgents.map(agent => ({
        agent_id: agent.agent_id,
        name: agent.name
      }));
      
      setAgents(agentsForDropdown);
    } catch (err) {
      console.error('Failed to fetch agents:', err);
    }
  }, [apiKey]);

  // Load all available (unassigned) numbers from ElevenLabs for attach flow
  const fetchAvailableElevenLabsNumbers = useCallback(async () => {
    if (!apiKey) return;
    try {
      setError(null);
      const resp = await fetch('https://api.elevenlabs.io/v1/convai/phone-numbers', {
        headers: {
          'xi-api-key': apiKey,
          'Content-Type': 'application/json',
        },
      });
      if (!resp.ok) {
        throw new Error(`Failed to list phone numbers: ${resp.statusText}`);
      }
      const data = await resp.json();
      const list: any[] = Array.isArray(data) ? data : (data.phone_numbers || data.items || []);
      const normalized: PhoneNumber[] = list.map((n: any) => ({
        phone_number: n.phone_number,
        provider: n.provider || 'twilio',
        label: n.label || '',
        phone_number_id: n.phone_number_id,
        assigned_agent: n.assigned_agent || (n.assigned_agent_id ? { agent_id: n.assigned_agent_id, agent_name: '' } : undefined)
      }));
      const unassigned = normalized.filter(n => !n.assigned_agent || !n.assigned_agent.agent_id);
      setAvailableNumbers(unassigned);
    } catch (err) {
      console.error('Failed to load available numbers:', err);
      setError(err instanceof Error ? err.message : 'Failed to load available numbers');
    }
  }, [apiKey]);

  const openAttachModal = async () => {
    setSelectedAttachPhoneId('');
    setSelectedAttachAgentId('');
    await fetchAvailableElevenLabsNumbers();
    setShowAttachModal(true);
  };

  const updatePhoneNumber = async (phoneNumberId: string, updates: any) => {
    try {
      const response = await fetch(`https://api.elevenlabs.io/v1/convai/phone-numbers/${phoneNumberId}`, {
        method: 'PATCH',
        headers: {
          'xi-api-key': apiKey,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updates),
      });

      if (!response.ok) {
        const errText = await response.text().catch(() => '');
        throw new Error(`Failed to update phone number: ${errText || response.statusText}`);
      }

      await fetchPhoneNumbers();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update phone number');
    }
  };

  // Attach selected ElevenLabs number to selected agent and track locally
  const attachPhoneNumberToAgent = async () => {
    if (!selectedAttachPhoneId || !selectedAttachAgentId) {
      setError('Please select a phone number and an agent.');
      return;
    }
    try {
      // 1) Assign in ElevenLabs (use agent_id per API)
      await updatePhoneNumber(selectedAttachPhoneId, { agent_id: selectedAttachAgentId });

      // 2) Fetch details to store tracking row
      const detailsResp = await fetch(`https://api.elevenlabs.io/v1/convai/phone-numbers/${selectedAttachPhoneId}`, {
        headers: {
          'xi-api-key': apiKey,
          'Content-Type': 'application/json',
        },
      });
      if (!detailsResp.ok) {
        throw new Error('Assigned, but failed to fetch phone details for tracking');
      }
      const details = await detailsResp.json();

      // 3) Track in Supabase for this user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('User not authenticated');
      }
      await supabase
        .from('user_phone_numbers')
        .upsert({
          user_id: user.id,
          elevenlabs_phone_number_id: selectedAttachPhoneId,
          phone_number: details.phone_number,
          label: details.label || '',
          provider: details.provider || 'twilio'
        }, { onConflict: 'elevenlabs_phone_number_id' });

      setShowAttachModal(false);
      await fetchPhoneNumbers();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to attach phone number');
    }
  };

  const deletePhoneNumber = async (phoneNumberId: string) => {
    if (!window.confirm('Are you sure you want to delete this phone number?')) return;

    try {
      // First verify the phone number belongs to the user
      const { data: userPhone, error: dbError } = await supabase
        .from('user_phone_numbers')
        .select('*')
        .eq('elevenlabs_phone_number_id', phoneNumberId)
        .single();

      if (dbError || !userPhone) {
        throw new Error('Phone number not found or you do not have permission to delete it');
      }

      // Delete from ElevenLabs
      const response = await fetch(`https://api.elevenlabs.io/v1/convai/phone-numbers/${phoneNumberId}`, {
        method: 'DELETE',
        headers: {
          'xi-api-key': apiKey,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to delete phone number: ${response.statusText}`);
      }

      // Remove tracking information from our database
      const { error: deleteDbError } = await supabase
        .from('user_phone_numbers')
        .delete()
        .eq('elevenlabs_phone_number_id', phoneNumberId);

      if (deleteDbError) {
        console.error('Error removing phone number tracking:', deleteDbError);
        setError('Phone number deleted but tracking cleanup failed. Please refresh.');
      }

      await fetchPhoneNumbers();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete phone number');
    }
  };

  const makeOutboundCall = async (phoneNumber: PhoneNumber, toNumber: string) => {
    try {
      const response = await fetch('https://api.elevenlabs.io/v1/convai/twilio/outbound_call', {
        method: 'POST',
        headers: {
          'xi-api-key': apiKey,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          agent_id: phoneNumber.assigned_agent?.agent_id,
          agent_phone_number_id: phoneNumber.phone_number_id,
          to_number: toNumber,
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to make outbound call: ${response.statusText}`);
      }

      const data = await response.json();
      alert(`Call initiated successfully! Call SID: ${data.callSid}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to make outbound call');
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    // retained for select controls in existing UI; no-op for removed form
  };

  const handleAgentAssignment = (phoneNumberId: string, agentId: string) => {
    const payload = agentId && agentId.trim() ? { agent_id: agentId } : { agent_id: null };
    updatePhoneNumber(phoneNumberId, payload);
  };

  const handleOutboundCall = (phoneNumber: PhoneNumber) => {
    const toNumber = prompt('Enter the phone number to call (with country code):');
    if (toNumber && phoneNumber.assigned_agent) {
      makeOutboundCall(phoneNumber, toNumber);
    } else if (!phoneNumber.assigned_agent) {
      alert('Please assign an agent to this phone number first.');
    }
  };

  useEffect(() => {
    fetchPhoneNumbers();
    fetchAgents();
  }, [fetchPhoneNumbers, fetchAgents]);

  if (!apiKey) {
    return (
      <div className="p-8 text-center">
        <div className="mb-6">
          <div className="text-6xl mb-4">🔑</div>
          <h3 className="text-xl font-semibold text-gray-300 mb-2">API Key Required</h3>
          <p className="text-gray-400">Please configure your API key in Settings to manage phone numbers.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-8">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-6 sm:mb-8 space-y-4 sm:space-y-0">
        <div>
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2">Phone Numbers</h2>
          <p className="text-gray-400 text-sm sm:text-base">Manage phone numbers for your agents and make outbound calls</p>
        </div>
        <button
          onClick={openAttachModal}
          className="btn-primary flex items-center justify-center space-x-2 w-full sm:w-auto"
        >
          <span>🔗</span>
          <span>Connect Existing Number</span>
        </button>
      </div>

      {error && (
        <div className="glass-card rounded-2xl p-4 mb-6 border border-red-500/30 neon-border">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <span className="text-red-400 text-xl">❌</span>
              <p className="text-red-300 text-sm sm:text-base">{error}</p>
            </div>
            <button onClick={() => setError(null)} className="text-red-400 hover:text-red-300">×</button>
          </div>
        </div>
      )}

      {loading && (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 sm:h-12 w-8 sm:w-12 border-b-2 border-blue-500 mb-4"></div>
          <p className="text-gray-300 text-base sm:text-lg">Loading phone numbers...</p>
        </div>
      )}

      {!loading && (
        <div className="space-y-4 sm:space-y-6">
          {phoneNumbers.length === 0 ? (
            <div className="text-center py-12 sm:py-16">
              <div className="text-6xl sm:text-8xl mb-4 sm:mb-6">📞</div>
              <h3 className="text-xl sm:text-2xl font-bold text-white mb-3 sm:mb-4">No phone numbers found</h3>
              <p className="text-gray-400 text-base sm:text-lg mb-6 sm:mb-8 max-w-md mx-auto px-4">
                Connect a phone number from your ElevenLabs account to enable voice calls for your agents.
              </p>
              <button
                onClick={openAttachModal}
                className="btn-primary text-base sm:text-lg px-6 sm:px-8 py-3 sm:py-4"
              >
                Connect a Phone Number
              </button>
            </div>
          ) : (
            phoneNumbers.map((phoneNumber) => (
              <div
                key={phoneNumber.phone_number_id}
                className="glass-card rounded-2xl p-4 sm:p-6 card-hover"
              >
                <div className="flex flex-col lg:flex-row lg:justify-between lg:items-start space-y-4 lg:space-y-0">
                  <div className="flex-1">
                    <div className="flex items-start space-x-3 sm:space-x-4 mb-4">
                      <div className="w-12 h-12 sm:w-14 sm:h-14 bg-blue-600 rounded-xl flex items-center justify-center flex-shrink-0">
                        <span className="text-white text-lg sm:text-2xl">📞</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-lg sm:text-xl font-bold text-white mb-1 break-all">
                          {phoneNumber.phone_number}
                        </h3>
                        <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-3 space-y-2 sm:space-y-0">
                          <span className="inline-flex items-center px-2 sm:px-3 py-1 rounded-full bg-blue-500/20 border border-blue-500/30 text-blue-300 text-xs sm:text-sm w-fit">
                            📡 {phoneNumber.provider}
                          </span>
                          <span className="text-gray-400 text-sm sm:text-base">{phoneNumber.label}</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="glass-effect rounded-xl p-3 sm:p-4 mb-4">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-3 space-y-2 sm:space-y-0">
                          <span className="text-gray-400 text-xs sm:text-sm">Assigned Agent:</span>
                          <select
                            value={phoneNumber.assigned_agent?.agent_id || ''}
                            onChange={(e) => handleAgentAssignment(phoneNumber.phone_number_id, e.target.value)}
                            className="w-full sm:w-auto bg-black/20 border border-white/20 rounded-lg px-2 sm:px-3 py-2 text-white text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="">No agent assigned</option>
                            {agents.map((agent) => (
                              <option key={agent.agent_id} value={agent.agent_id}>
                                {agent.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>

                    {phoneNumber.assigned_agent && (
                      <div className="flex items-center space-x-2 mb-4">
                        <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                        <span className="text-green-400 text-xs sm:text-sm font-medium">
                          Active with {phoneNumber.assigned_agent.agent_name}
                        </span>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex flex-row lg:flex-col lg:items-end space-x-2 lg:space-x-0 lg:space-y-3 lg:ml-6">
                    {phoneNumber.assigned_agent && (
                      <button
                        onClick={() => handleOutboundCall(phoneNumber)}
                        className="flex-1 lg:flex-none btn-primary px-3 sm:px-4 py-2 flex items-center justify-center space-x-1 sm:space-x-2 text-xs sm:text-sm"
                      >
                        <span>📞</span>
                        <span className="hidden sm:inline">Make Call</span>
                        <span className="sm:hidden">Call</span>
                      </button>
                    )}
                    <button
                      onClick={() => deletePhoneNumber(phoneNumber.phone_number_id)}
                      className="flex-1 lg:flex-none glass-effect hover:bg-red-500/20 border border-red-500/30 text-red-300 hover:text-red-200 px-3 sm:px-4 py-2 rounded-xl transition-all duration-300 text-xs sm:text-sm"
                    >
                      <span className="lg:hidden">🗑️</span>
                      <span className="hidden lg:inline">🗑️ Delete</span>
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Attach Existing Phone Number Modal */}
      {showAttachModal && createPortal(
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="glass-card rounded-2xl max-w-xl w-full border border-white/10">
            <div className="p-8">
              <div className="flex justify-between items-center mb-8">
                <div>
                  <h2 className="text-2xl font-bold text-white mb-2">Connect Existing Number</h2>
                  <p className="text-gray-400">Select a number from your ElevenLabs account and attach it to one of your agents</p>
                </div>
                <button
                  onClick={() => setShowAttachModal(false)}
                  className="text-gray-400 hover:text-white text-2xl transition-colors hover:bg-white/10 rounded-lg p-2"
                >
                  ×
                </button>
              </div>

              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Select Phone Number *</label>
                  <select
                    value={selectedAttachPhoneId}
                    onChange={(e) => setSelectedAttachPhoneId(e.target.value)}
                    className="w-full px-4 py-3 bg-black/20 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Choose an unassigned number...</option>
                    {availableNumbers.map((n) => (
                      <option key={n.phone_number_id} value={n.phone_number_id}>
                        {n.phone_number} {n.label ? `• ${n.label}` : ''}
                      </option>
                    ))}
                  </select>
                  <button onClick={fetchAvailableElevenLabsNumbers} className="mt-2 text-xs text-blue-300 hover:text-blue-200">↻ Refresh list</button>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Attach To Agent *</label>
                  <select
                    value={selectedAttachAgentId}
                    onChange={(e) => setSelectedAttachAgentId(e.target.value)}
                    className="w-full px-4 py-3 bg-black/20 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select agent...</option>
                    {agents.map((a) => (
                      <option key={a.agent_id} value={a.agent_id}>{a.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex justify-end space-x-4 pt-8 border-t border-white/10 mt-8">
                <button
                  onClick={() => setShowAttachModal(false)}
                  className="btn-secondary px-6 py-3"
                >
                  Cancel
                </button>
                <button
                  onClick={attachPhoneNumberToAgent}
                  className="btn-primary px-6 py-3"
                >
                  🔗 Connect Number
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default PhoneNumbers; 